import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import '../../assets/styles/Workflow.css';

interface DateRangeFilterDropdownProps {
  label: string;
  from: string;
  to: string;
  onChange: (from: string, to: string) => void;
}

const PANEL_WIDTH = 240;

// Same funnel-button + portal-positioned popover as ColumnFilterDropdown, but
// for an arbitrary from/to date range instead of a checkbox list of distinct
// values - "Created At" needs a range, not a fixed set of options, so it
// can't reuse that component's body as-is.
const DateRangeFilterDropdown: React.FC<DateRangeFilterDropdownProps> = ({ label, from, to, onChange }) => {
  const [open, setOpen] = useState(false);
  const [draftFrom, setDraftFrom] = useState(from);
  const [draftTo, setDraftTo] = useState(to);
  const [coords, setCoords] = useState<{ top?: number; bottom?: number; left: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) { setDraftFrom(from); setDraftTo(to); }
  }, [open, from, to]);

  useLayoutEffect(() => {
    if (!open || !buttonRef.current) return;
    const place = () => {
      if (!buttonRef.current) return;
      const btnRect = buttonRef.current.getBoundingClientRect();
      const panelHeight = panelRef.current?.offsetHeight ?? 0;
      const spaceBelow = window.innerHeight - btnRect.bottom;
      const left = Math.min(btnRect.left, window.innerWidth - PANEL_WIDTH - 8);
      if (panelHeight > 0 && spaceBelow < panelHeight + 8 && btnRect.top > spaceBelow) {
        setCoords({ bottom: window.innerHeight - btnRect.top + 6, left });
      } else {
        setCoords({ top: btnRect.bottom + 6, left });
      }
    };
    place();
    const raf = requestAnimationFrame(place);
    return () => cancelAnimationFrame(raf);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (buttonRef.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    const handleScrollOrResize = (e: Event) => {
      const target = e.target as Node;
      if (panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [open]);

  const apply = () => {
    onChange(draftFrom, draftTo);
    setOpen(false);
  };

  const clear = () => {
    setDraftFrom(''); setDraftTo('');
    onChange('', '');
    setOpen(false);
  };

  const active = !!(from || to);

  return (
    <span className="wf-colfilter">
      <button
        ref={buttonRef}
        type="button"
        className={`wf-colfilter-btn${active ? ' active' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-label={`Filter ${label}`}
        title={`Filter ${label}`}
      >
        &#9660;{active && <span className="wf-colfilter-count">•</span>}
      </button>
      {open && createPortal(
        <div
          ref={panelRef}
          className="wf-colfilter-panel-portal"
          style={{
            left: coords?.left ?? -9999,
            top: coords?.top,
            bottom: coords?.bottom,
            visibility: coords ? 'visible' : 'hidden',
            width: PANEL_WIDTH,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <label style={{ fontSize: 11.5, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' }}>
              From
              <input
                type="date"
                value={draftFrom}
                max={draftTo || undefined}
                onChange={(e) => setDraftFrom(e.target.value)}
                style={{ display: 'block', width: '100%', marginTop: 4 }}
              />
            </label>
            <label style={{ fontSize: 11.5, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' }}>
              To
              <input
                type="date"
                value={draftTo}
                min={draftFrom || undefined}
                onChange={(e) => setDraftTo(e.target.value)}
                style={{ display: 'block', width: '100%', marginTop: 4 }}
              />
            </label>
          </div>
          <div className="wf-colfilter-actions">
            <button type="button" className="wf-btn" onClick={clear}>Clear</button>
            <button type="button" className="wf-btn wf-btn-primary" onClick={apply}>Apply</button>
          </div>
        </div>,
        document.body
      )}
    </span>
  );
};

export default DateRangeFilterDropdown;
